// Nomina del proveedor en la planta seleccionada (PP-22): dos solapas,
// vehiculos y conductores, con alta, edicion, baja y reincorporacion. El proveedor mantiene su
// nomina sin pedirle nada a UCORP. Cada fila abre su ficha de documentos
// (PP-23) para cargar lo que falta y renovar lo que vence.
//
// Que NO muestra la lista, a proposito:
// - telefono, documento ni domicilio del conductor (PII minima por pantalla;
//   el telefono y el documento solo aparecen en el formulario).
// - integrantes de otro proveedor: la API filtra por la habilitacion de la
//   cuenta y el cliente lo repite (fleet/http.ts).
//
// El titular si se muestra (columna Titular): desde contratos 0.6.7 la API lo
// manda en la lectura del proveedor y nunca en la de la planta (ADR-014 §7).
// Las bajas se reincorporan desde la fila, con confirmacion.
//
// Las solapas viven en RosterTabs, compartidas con la vista de todas las
// plantas (AllSitesFleet, PP-70).
import { useCallback, useId, useState } from "react";
import type { Driver, SiteVehicle } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Button } from "../components/ui/button.js";
import { VEHICLE_TYPE_LABEL } from "../logic/fleet.js";
import type { SiteAccess } from "../sites/port.js";
import { SubjectDocuments } from "./documents/SubjectDocuments.js";
import { DriverForm, VehicleForm } from "./forms.js";
import { EligibilityBadge, HolderCell, RosterBadge, SiteStatusBadge, useHolders } from "./parts.js";
import type { FleetPort } from "./port.js";
import { RosterPanel, type Column } from "./RosterPanel.js";
import { RosterTabs, type RosterTab } from "./RosterTabs.js";
import { useRoster, type RosterState } from "./useRoster.js";

interface Props {
  session: PortalSession;
  port: FleetPort;
  site: SiteAccess;
  /** Sin `onBack` no hay boton de volver: el shell del portal navega por la barra superior. */
  onBack?: () => void;
}

const VEHICLE_COLUMNS: readonly Column<SiteVehicle>[] = [
  { header: "Código", cell: (v) => <span className="tabular font-semibold">{v.code}</span> },
  {
    header: "Vehículo",
    cell: (v) => (
      <div className="flex flex-col">
        <span className="tabular">{v.plate}</span>
        <span className="text-xs text-texto-suave">
          {VEHICLE_TYPE_LABEL[v.type]} · {v.capacity} pasajeros{v.year === undefined ? "" : ` · ${String(v.year)}`}
        </span>
      </div>
    ),
  },
  { header: "Titular", cell: (v) => <HolderCell holder={v.holder} /> },
  { header: "En la planta", cell: (v) => <SiteStatusBadge active={v.active} /> },
  { header: "Nómina", cell: (v) => <RosterBadge inRoster={v.inRoster} /> },
  { header: "Elegibilidad", cell: (v) => <EligibilityBadge eligibility={v.eligibility} /> },
];

const DRIVER_COLUMNS: readonly Column<Driver>[] = [
  { header: "Código", cell: (d) => <span className="tabular font-semibold">{d.code}</span> },
  { header: "Nombre", cell: (d) => d.name },
  { header: "Titular", cell: (d) => <HolderCell holder={d.holder} /> },
  { header: "En la planta", cell: (d) => <SiteStatusBadge active={d.active} /> },
  { header: "Nómina", cell: (d) => <RosterBadge inRoster={d.inRoster} /> },
  { header: "Elegibilidad", cell: (d) => <EligibilityBadge eligibility={d.eligibility} /> },
];

/** Cuantos estan en la nomina (las bajas no cuentan). */
function count<T extends { inRoster: boolean }>(state: RosterState<T>): string {
  return state.estado === "listo" ? ` (${String(state.items.filter((row) => row.inRoster).length)})` : "";
}

/** YYYY-MM-DD a DD/MM/YYYY sin pasar por Date: la fecha ya es la operativa de la planta. */
function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${day ?? ""}/${month ?? ""}/${year ?? ""}`;
}

export function Fleet({ session, port, site, onBack }: Props): React.JSX.Element {
  const [tab, setTab] = useState<RosterTab>("vehicles");
  const baseId = useId();
  const fleetSite = { siteId: site.siteId, providerId: site.providerId };

  const loadVehicles = useCallback((s: PortalSession, target: typeof fleetSite) => port.listVehicles(s, target), [port]);
  const loadDrivers = useCallback((s: PortalSession, target: typeof fleetSite) => port.listDrivers(s, target), [port]);
  const vehicles = useRoster(loadVehicles, session, fleetSite);
  const drivers = useRoster(loadDrivers, session, fleetSite);
  const holders = useHolders(port, session, fleetSite);

  const today = vehicles.state.estado === "listo" ? vehicles.state.today : drivers.state.estado === "listo" ? drivers.state.today : undefined;

  const label: Record<RosterTab, string> = {
    vehicles: `Vehículos${count(vehicles.state)}`,
    drivers: `Conductores${count(drivers.state)}`,
  };

  return (
    <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="m-0 text-eyebrow font-semibold uppercase tracking-eyebrow text-acero-texto">{site.name}</p>
          <h1 id={`${baseId}-title`} className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">
            Nómina
          </h1>
          <p className="m-0 text-xs text-texto-suave">
            {today === undefined
              ? "Vehículos y conductores de la empresa en esta planta."
              : `Elegibilidad calculada al ${formatDate(today)}, fecha operativa de la planta.`}
          </p>
        </div>
        {onBack !== undefined && (
          <Button variant="secundario" size="sm" onClick={onBack}>
            Volver a plantas
          </Button>
        )}
      </div>

      <RosterTabs baseId={baseId} tab={tab} onChange={setTab} label={label}>
        {tab === "vehicles" ? (
          <RosterPanel
            key={`vehicles-${site.siteId}-${site.providerId}`}
            noun="vehículo"
            plural="los vehículos"
            emptyCopy="La empresa todavía no tiene vehículos en la nómina de esta planta. Usar Alta de vehículo para cargar el primero."
            roster={vehicles}
            columns={VEHICLE_COLUMNS}
            rowLabel={(row) => `Vehículo ${row.code} · ${row.plate}`}
            remove={(row) => port.deactivateVehicle(session, fleetSite, row.id)}
            reinstate={(row) => port.reinstateVehicle(session, fleetSite, row.id)}
            renderDocuments={(row, onClose) => (
              <SubjectDocuments
                key={row.id}
                session={session}
                port={port}
                site={fleetSite}
                subject={{ type: "vehicle", providerId: row.providerId, id: row.id }}
                subjectLabel={`Vehículo ${row.code} · ${row.plate}`}
                eligibility={row.eligibility}
                onChanged={vehicles.refresh}
                onClose={onClose}
              />
            )}
            renderForm={({ current, onSaved, onCancel }) => (
              <VehicleForm
                {...(current === undefined ? {} : { current })}
                holders={holders}
                create={(body) => port.createVehicle(session, fleetSite, body)}
                update={(id, body) => port.updateVehicle(session, fleetSite, id, body)}
                onSaved={onSaved}
                onCancel={onCancel}
              />
            )}
          />
        ) : (
          <RosterPanel
            key={`drivers-${site.siteId}-${site.providerId}`}
            noun="conductor"
            plural="los conductores"
            emptyCopy="La empresa todavía no tiene conductores en la nómina de esta planta. Usar Alta de conductor para cargar el primero."
            roster={drivers}
            columns={DRIVER_COLUMNS}
            rowLabel={(row) => `Conductor ${row.code} · ${row.name}`}
            remove={(row) => port.deactivateDriver(session, fleetSite, row.id)}
            reinstate={(row) => port.reinstateDriver(session, fleetSite, row.id)}
            renderDocuments={(row, onClose) => (
              <SubjectDocuments
                key={row.id}
                session={session}
                port={port}
                site={fleetSite}
                subject={{ type: "driver", providerId: row.providerId, id: row.id }}
                subjectLabel={`Conductor ${row.code} · ${row.name}`}
                eligibility={row.eligibility}
                onChanged={drivers.refresh}
                onClose={onClose}
              />
            )}
            renderForm={({ current, onSaved, onCancel }) => (
              <DriverForm
                {...(current === undefined ? {} : { current })}
                holders={holders}
                create={(body) => port.createDriver(session, fleetSite, body)}
                update={(id, body) => port.updateDriver(session, fleetSite, id, body)}
                onSaved={onSaved}
                onCancel={onCancel}
              />
            )}
          />
        )}
      </RosterTabs>
    </section>
  );
}
